import { useEffect } from 'react';
import { Paper } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { observer } from 'mobx-react';
import { QuoteTypeStore } from '../../../stores/quoteTypeStore';
import { FORM_MAX_WIDTH, PreflightStep } from '../../../utils/constants';
import { ClosingForm } from '../../summary/components/closing/closingForm';
import { PrefligthCard } from './preflightCard';
import { BRIDGE, LoanSubType, stateMapping, TERM } from '@roc/feature-utils';
import { useBaseStore } from '@roc/feature-app-core';
import {
  UpdateOutlined,
  ExpandMore,
  HomeWorkOutlined,
} from '@material-ui/icons';

import { useState } from 'react';
import {
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
} from '@material-ui/core';
import {
  getBooleanDisplayValue,
  getValueOrDefaultDash,
  parseDate,
  formatPercentageValue,
} from 'libs/feature-one-tool/src/quote/utils/utils';
import { ClosingStore } from 'libs/feature-one-tool/src/quote/stores/closing/closingStore';
import { insertIf } from '@roc/feature-utils';
import {
  DetailsTable,
  DetailsTableField,
} from '../../../components/detailsTable';
import { RoundedBackgroundIcon } from '../../../components/roundedBackgroundIcon';
import { ViewControls } from '../../../components/viewControls';
import { TitleDivider } from '../../../components/titleDivider';
import { AppraisalQuestionnaire } from 'libs/feature-documents/src/documents/components/documentForms/appraisalDetailsV2/components/appraisalQuestionnaireModal';
import { ItemDisplay } from '../../../components/itemDisplay';
import { AppraisalQuestionnaireV2FormStore } from 'libs/feature-documents/src/documents/stores/documentForms/appraisalDetailsV2/appraisalQuestonnaireV2FormStore';
import { ValuationProperty } from 'libs/feature-documents/src/documents/stores/documentForms/appraisalDetailsV2/appraisalDetailsV2Store';

export const PrefligthValuationCard = observer(({ store }) => {
  const entityFormStore = store.entityStore.entityFormStore;
  const questionnaireFormStore =
    store.preflightStore.appraisalQuestionnaireFormStore;
  const properties = store.propertiesStore.getProperties().map(p => ({
    propertyId: p.propertyId,
    propertyAddress: p.address,
    uuid: p.uuid,
  }));
  const editMode = entityFormStore.form.meta.editMode;
  const { globalStore } = useBaseStore();
  const allowParticipation = globalStore.userFeatures?.allowParticipation;
  const otherAmcAllowed = globalStore.userFeatures?.otherAmcAllowed;
  const radioVisible = otherAmcAllowed === true || globalStore.isInternalPortal;
  const { preflightStore } = store;

  useEffect(() => {
    const props = store.propertiesStore.getProperties();
    questionnaireFormStore.getAmcOptions({
      lenderId: store.lenderId,
      loanSubtype: store.loanSubtype,
      properties: props.map((p: any) => ({
        state: p.state,
        county: p.county,
        propertyType: p.propertyType,
        unitCount: p.units ?? 1,
      })),
    });
  }, []);

  const step = PreflightStep.APPRAISAL;

  return (
    <PrefligthCard
      store={store}
      step={step}
      onEdit={() => store.preflightStore.goToStep(step)}
      onReset={radioVisible ? () => questionnaireFormStore.reset() : undefined}
      onSave={() => {
        if (questionnaireFormStore.validateQuestionnaire(properties)) {
          store.preflightStore.goToNextStep();
        }
      }}
      FormComponent={() => (
        <Grid container spacing={2} justifyContent="center" alignItems="center">
          <Grid item container spacing={2} style={{ maxWidth: FORM_MAX_WIDTH }}>
            <Grid container justifyContent="space-between" alignItems="center">
              <Grid item xs={6}>
                <Grid
                  container
                  spacing={2}
                  justifyContent="flex-start"
                  alignItems="center"
                >
                  <Grid item alignItems="center" style={{ height: '100%' }}>
                    <RoundedBackgroundIcon
                      Icon={HomeWorkOutlined}
                      iconColor="#6D6D6D"
                    />
                  </Grid>
                  <Grid item>
                    <Typography variant="h5">Valuation</Typography>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <AppraisalQuestionnaire
                properties={properties}
                questionnaireFormStore={questionnaireFormStore}
                otherAmcAllowed={otherAmcAllowed}
                loanQuoteId={store.quoteId}
              />
            </Grid>
          </Grid>
        </Grid>
      )}
      AccordionComponent={props => (
        <AppraisalAccordion
          {...props}
          properties={properties}
          questionnaireFormStore={questionnaireFormStore}
        />
      )}
    />
  );
});

interface AppraisalAccordionProps {
  properties: ValuationProperty[];
  questionnaireFormStore: AppraisalQuestionnaireV2FormStore;
  expanded: boolean;
  onExpand: (expanded: boolean) => void;
  onEdit: () => void;
}

export const AppraisalAccordion = observer(
  ({
    properties,
    questionnaireFormStore,
    expanded,
    onExpand,
    onEdit,
  }: AppraisalAccordionProps) => {
    const providingOwnAppraisal =
      questionnaireFormStore.form.fields.providingOwnAppraisal.value;
    const selectedAmc = questionnaireFormStore.form.fields.selectedAmc.value;
    const pdfUploadName = Object.values(questionnaireFormStore.pdfUploads)?.map(upload => upload?.name)[0] ?? ''
    const xmlUploadName = Object.values(questionnaireFormStore.xmlUploads)?.map(upload => upload?.name)[0] ?? ''

    const appraisalDetails: DetailsTableField[] = [
      {
        label: 'Providing Own Valuation?',
        value: getValueOrDefaultDash(
          providingOwnAppraisal === null ? null : providingOwnAppraisal ? 'Yes' : 'No'
        ),
        colSpan: 2,
      },
      ...insertIf(providingOwnAppraisal === false, [
        {
          label: 'Selected AMC',
          value: getValueOrDefaultDash(selectedAmc),
          colSpan: 2,
        },
      ]),
    ];

    return (
      <Accordion
        expanded={expanded}
        onChange={(e, expanded) => onExpand(expanded)}
        elevation={0}
      >
        <AccordionSummary
          expandIcon={<ExpandMore fontSize="large" />}
          aria-controls={`borrower-information-accordion-${1}`}
        >
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid item xs={6}>
              <Grid
                container
                spacing={2}
                justifyContent="flex-start"
                alignItems="center"
              >
                <Grid item alignItems="center" style={{ height: '100%' }}>
                  <RoundedBackgroundIcon
                    Icon={HomeWorkOutlined}
                    iconColor="#6D6D6D"
                  />
                </Grid>
                <Grid item>
                  <Typography
                    variant="h5"
                    style={{ fontSize: '20px', fontWeight: 'bold' }}
                  >
                    Valuation
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={6}></Grid>
          </Grid>
        </AccordionSummary>
        <AccordionDetails>
          <Grid
            container
            spacing={2}
            alignItems="center"
            style={{ marginLeft: '58px' }}
          >
            <Grid item xs={12}>
              <Grid
                container
                spacing={2}
                justifyContent="flex-end"
                alignItems="center"
              >
                <ViewControls showRemoveButton={false} onEditClick={onEdit} />
              </Grid>
            </Grid>

            <Grid item xs={12} style={{ marginBottom: 10, marginTop: 10 }}>
              <TitleDivider title="Valuation Details" />
            </Grid>
            <DetailsTable fields={appraisalDetails} />
            {providingOwnAppraisal && (
              <>
                {(properties || []).map(property => (
                  <>
                    <Grid
                      item
                      xs={12}
                      style={{ marginBottom: 10, marginTop: 10 }}
                    >
                      <TitleDivider
                        title={`Uploads - ${property.propertyAddress}`}
                      />
                    </Grid>
                    <DetailsTable
                      fields={[
                        {
                          label: `Valuation PDF Document`,
                          value: getValueOrDefaultDash(pdfUploadName),
                          colSpan: 1,
                        },
                        {
                          label: `Valuation XML Document`,
                          value: getValueOrDefaultDash(xmlUploadName),
                          colSpan: 1,
                        },
                      ]}
                    />
                  </>
                ))}
              </>
            )}
          </Grid>
        </AccordionDetails>
      </Accordion>
    );
  }
);
